
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTimeEntries } from '@/hooks/useTimeEntries';
import { useProjects } from '@/hooks/useProjects';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RefreshCw } from 'lucide-react';
import { getCurrentUser as getCurrentUserFromAuth, type CurrentUser } from '@/utils/authUtils';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

const RecentTimeEntries = () => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { timeEntries, loading, refetch } = useTimeEntries(currentUser?.id);
  const { projects } = useProjects();

  useEffect(() => {
    const getCurrentUser = async () => {
      const user = await getCurrentUserFromAuth();
      setCurrentUser(user);
    };
    getCurrentUser();
  }, []);

  // Auto-refresh toutes les 30 secondes si une session est active
  useEffect(() => {
    if (!currentUser) return;

    const checkAndRefresh = async () => {
      try {
        let hasActive = false;

        if (shouldUsePinMode()) {
          const pin = getUserPinOrThrow();
          const { data, error } = await supabase.rpc('pin_list_user_time_entries', {
            p_pin: pin,
            p_user_id: currentUser.id,
            p_start: null,
            p_end: null,
            p_limit: 25,
            p_offset: 0,
          });
          if (error) throw error;
          hasActive = Boolean((data || []).find((e: any) => !e.end_time));
        } else {
          const { data: activeSession, error } = await supabase
            .from('time_entries')
            .select('id')
            .eq('user_id', currentUser.id)
            .is('end_time', null)
            .limit(1);
          if (error) throw error;
          hasActive = Boolean(activeSession && activeSession.length > 0);
        }

        if (hasActive) {
          // Il y a une session active, rafraîchir les données
          await refetch();
        }
      } catch (error) {
        console.error('Erreur auto-refresh:', error);
      }
    };

    const interval = setInterval(checkAndRefresh, 30000); // 30 secondes
    return () => clearInterval(interval);
  }, [currentUser, refetch]);

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project ? `${project.name} - ${project.client_name}` : 'Projet inconnu';
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes || minutes === 0) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <div className="text-sm text-muted-foreground">Chargement des entrées...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const recentEntries = timeEntries.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Entrées Récentes</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {recentEntries.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Aucune entrée de temps trouvée</p>
            <p className="text-sm text-gray-500 mt-2">
              Commencez votre premier shift pour voir vos données ici !
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentEntries.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <p className="font-medium">{getProjectName(entry.project_id)}</p>
                  {entry.description && (
                    <p className="text-sm text-muted-foreground">{entry.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(entry.start_time), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </p>
                  {!entry.end_time && (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-green-600 font-medium">En cours...</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <Badge variant={entry.duration_minutes ? "default" : "secondary"}>
                    {entry.end_time ? formatDuration(entry.duration_minutes) : 'En cours'}
                  </Badge>
                  {entry.is_billable && (
                    <p className="text-xs text-green-600 mt-1">💰 Facturable</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Stats rapides */}
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs text-gray-500 text-center">
            📊 Total: {timeEntries.length} entrées de temps
            {timeEntries.some(e => !e.end_time) && (
              <div className="text-green-600 mt-1">
                ⚡ Session active détectée
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentTimeEntries;
